import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import ScrollReveal from "@/components/ScrollReveal";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Zap, Target, Calendar, HelpCircle, CheckCircle } from "lucide-react";

const Services = () => {
  const navigate = useNavigate();

  return (
    <Layout>
      <SEOHead title="AI Chatbot for Small Business Websites | Xentic Solutions" description="Xentic builds AI chatbots that respond instantly, qualify leads, book appointments, and handle FAQs, all on autopilot." path="/services" />

      {/* Hero */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute top-1/4 -right-32 w-96 h-96 bg-primary/15 rounded-full blur-[120px] animate-blob" />
        <div className="container text-center max-w-3xl relative z-10">
          <ScrollReveal>
            <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Our Services</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold leading-tight">Your Business, Responding Instantly <span className="text-gradient">Without You</span></h1>
            <p className="mt-6 text-lg text-muted-foreground">Our AI chatbots work 24/7 to capture leads, answer questions, and book appointments so you never miss an opportunity.</p>
          </ScrollReveal>
        </div>
      </section>

      {/* 4 Core Capabilities */}
      <section className="py-20 section-alt">
        <div className="container">
          <ScrollReveal>
            <p className="text-center text-sm uppercase tracking-widest text-muted-foreground mb-3">Capabilities</p>
            <h2 className="font-heading text-3xl font-bold text-center mb-12">Core Capabilities</h2>
          </ScrollReveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Zap, title: "Instant Responses", desc: "Respond to every lead in under 3 seconds 24/7 across your website, social media, WhatsApp, SMS & more" },
              { icon: Target, title: "Lead Qualification", desc: "Automatically ask the right questions and score leads before they reach your team." },
              { icon: Calendar, title: "Appointment Booking", desc: "Let prospects book directly through the chatbot. No back-and-forth emails." },
              { icon: HelpCircle, title: "FAQ Automation", desc: "Answer the 20 questions you get asked every day without lifting a finger." },
            ].map((c, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <Card className="h-full">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <c.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-heading font-bold mb-2">{c.title}</h3>
                    <p className="text-sm text-muted-foreground">{c.desc}</p>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-20">
        <div className="container max-w-3xl">
          <ScrollReveal>
            <h2 className="font-heading text-3xl font-bold text-center mb-8">What Happens After You Go Live</h2>
          </ScrollReveal>
          <div className="space-y-4">
            {[
              "40 to 60 percent increase in lead capture within the first month",
              "80% of routine questions handled automatically",
              "Average response time drops from hours to under 2 seconds",
              "Front desk and support staff freed up for high-value work",
              "24/7 availability without overtime costs",
            ].map((r, i) => (
              <ScrollReveal key={i} delay={i * 0.05}>
                <div className="flex items-start gap-3 glass-card-hover rounded-lg p-4">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <p className="text-sm">{r}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 section-alt">
        <div className="container">
          <ScrollReveal>
            <p className="text-center text-sm uppercase tracking-widest text-muted-foreground mb-3">Industries</p>
            <h2 className="font-heading text-3xl font-bold text-center mb-12">Built For Your Industry</h2>
          </ScrollReveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { ind: "HVAC", use: "Capture emergency service requests after hours and book technician visits automatically." },
              { ind: "Healthcare", use: "Let patients schedule appointments, ask about services, and get directions instantly." },
              { ind: "Real Estate", use: "Qualify buyers and sellers 24/7 and schedule viewings without manual follow-up." },
              { ind: "Legal", use: "Pre-qualify potential clients by practice area and urgency before consultation." },
              { ind: "E-Commerce", use: "Answer product questions, handle returns, and upsell all through chat." },
              { ind: "Education", use: "Automate enrollment inquiries, course info, and campus visit scheduling." },
            ].map((item, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <Card className="h-full">
                  <CardContent className="p-6">
                    <h3 className="font-heading font-bold mb-2">{item.ind}</h3>
                    <p className="text-sm text-muted-foreground">{item.use}</p>
                  </CardContent>
                </Card>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 relative overflow-hidden" style={{ background: "linear-gradient(135deg, rgba(0,217,255,0.08), rgba(79,70,229,0.08))" }}>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[250px] bg-primary/10 rounded-full blur-[100px]" />
        <div className="container text-center relative z-10">
          <ScrollReveal>
            <h2 className="font-heading text-3xl font-bold mb-4">Ready to Never Miss a Lead Again?</h2>
            <p className="text-muted-foreground mb-8 max-w-lg mx-auto">Book a free consultation and we'll show you exactly how an AI chatbot fits your business.</p>
            <Button
              size="lg"
              className="border-0 hover:-translate-y-0.5 transition-transform duration-300 glow-shadow-sm"
              style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}
              onClick={() => navigate("/contact")}
            >
              <span className="text-white font-semibold">Book Free Consultation →</span>
            </Button>
          </ScrollReveal>
        </div>
      </section>
    </Layout>
  );
};

export default Services;
